import { useRef, useState } from "react";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { IconBadge } from "@/shared/components/ui/IconBadge";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import {
  LucideBell,
  LucideClipboardList,
  LucideClock,
  LucideGlobe,
  LucideInfo,
  LucideLink,
  LucideMessageSquareHeart,
  LucideMail,
  LucidePhone,
  LucideSparkles,
  LucideUserRound,
  LucideWallet,
} from "lucide-react";

const groups = ["Appointments", "Business", "Payments", "Communication", "Account & support"] as const;

export function AppsList() {
  const navigation = useAppNavigation();
  const { user } = useAuth();
  const { business } = useCurrentBusiness();
  const [query, setQuery] = useState("");
  const searchContainer = useRef<HTMLDivElement>(null);

  const destinations = [
    { group: "Appointments", title: "Services", description: "Prices, durations and service details", icon: LucideClipboardList, onClick: () => navigation.goToServices() },
    { group: "Appointments", title: "Working hours", description: "Availability and weekly time intervals", icon: LucideClock, onClick: navigation.goToAvailabilities },
    { group: "Appointments", title: "Appointment rules", description: "Customer cancellation, rescheduling and booking notice", icon: LucideClipboardList, onClick: navigation.goToAppointmentRules },
    { group: "Appointments", title: "Forms", description: "Preview forms and attach them to services", icon: LucideClipboardList, onClick: navigation.goToForms },
    { group: "Business", title: "Profile & business details", description: "Your profile, business and location details", icon: LucideUserRound, onClick: navigation.goToProfile },
    {
      group: "Business",
      title: "Website",
      description: "Your logo, description and booking link",
      icon: LucideGlobe,
      onClick: navigation.goToWebsite,
    },
    ...(user && business?.ownerId === user.id ? [
      { group: "Payments", title: "Finances", description: "Income and payments from Stripe", icon: LucideWallet, onClick: navigation.goToFinances },
      { group: "Payments", title: "Stripe setup", description: "Set up your US business Stripe account", icon: LucideLink, onClick: navigation.goToStripe },
    ] : []),
    { group: "Communication", title: "Notifications", description: "Booking updates and cancellation receipts", icon: LucideBell, onClick: navigation.goToNotifications },
    { group: "Communication", title: "Customer reminders", description: "Appointment reminders and customer emails", icon: LucideMail, onClick: navigation.goToCustomerEmails },
    ...(user && business?.ownerId === user.id ? [
      { group: "Communication", title: "Phone number", description: "Your business number and call forwarding", icon: LucidePhone, onClick: navigation.goToPhoneNumber },
    ] : []),
    ...(user && business?.ownerId === user.id ? [
      { group: "Account & support", title: "Plan", description: "Pro and Pro+AI features", icon: LucideSparkles, onClick: navigation.goToPlan },
    ] : []),
    { group: "Account & support", title: "Connected apps", description: "Review and disconnect account access", icon: LucideLink, onClick: navigation.goToConnectedApps },
    { group: "Account & support", title: "Feedback", description: "Share ideas or report a problem", icon: LucideMessageSquareHeart, onClick: navigation.goToFeedback },
    { group: "Account & support", title: "About", description: "Version, privacy and terms", icon: LucideInfo, onClick: navigation.goToAbout },
  ];
  const search = query.trim().toLowerCase();
  const visible = destinations.filter(({ title, description, group }) =>
    `${title} ${description} ${group}`.toLowerCase().includes(search)
  );

  return (
    <div className="space-y-6">
      <div ref={searchContainer}>
        <ListSearch
          label="Find a destination"
          placeholder="Search More"
          value={query}
          onChange={setQuery}
        />
      </div>
      {visible.length === 0 ? (
        <EmptyState
          title="No destinations found"
          message="Try Services, Working hours or Profile & business details."
          action={{
            label: "Show all destinations",
            variant: "outline",
            onClick: () => {
              setQuery("");
              searchContainer.current?.querySelector("input")?.focus();
            },
          }}
        />
      ) : groups.map((group) => {
        const items = visible.filter((item) => item.group === group);
        if (!items.length) return null;
        return (
          <Section key={group} title={group}>
            <div className="space-y-2">
              {items.map(({ title, description, icon: Icon, onClick }) => (
                <InfoRow
                  key={title}
                  title={title}
                  subtitle={description}
                  icon={<IconBadge tone="primary"><Icon aria-hidden="true" /></IconBadge>}
                  searchText={query.trim()}
                  onClick={onClick}
                />
              ))}
            </div>
          </Section>
        );
      })}
    </div>
  );
}
